import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from './api';
import type { GuildSummary, MeResponse } from './types';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/auth/me'),
    // Rights can change while someone is logged in (Zugriff page) — the API
    // enforces them immediately, this keeps the sidebar/buttons in step.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: (failureCount, err) => {
      // Don't retry 401s — user just isn't logged in.
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 2;
    },
  });
}

/** Scrolls to the element the URL hash names (`#id`) once the page is `ready`. */
export function useScrollToHash(ready: boolean) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!ready || !hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [ready, hash]);
}

/**
 * Name + avatar of the bot (public, works before login) — the dashboard's
 * branding: browser tab, icon rail, login page. Comes live from Discord.
 */
export function useBotProfile() {
  return useQuery({
    queryKey: ['bot-profile'],
    queryFn: () => api<{ name: string; avatarUrl: string | null }>('/api/bot/profile'),
    staleTime: 10 * 60_000,
    retry: 1,
  });
}

export function useGuilds(enabled: boolean) {
  return useQuery({
    queryKey: ['guilds'],
    queryFn: () => api<{ guilds: GuildSummary[] }>('/api/guilds'),
    enabled,
  });
}
